/**
 * AppLauncher.tsx
 *
 * App launcher for the "Test Like A Mobile" phone UI.
 * Displays 3 app icons (Phone, Messaging, Browser) and handles navigation.
 *
 * Features:
 * - Material Design 3 app grid
 * - 3 apps: Phone Dialer, Messaging, Browser
 * - App icon with labels
 * - Navigation to app on click
 * - Disabled state when network not registered
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Phone, MessageSquare, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PhoneDialer } from './apps/PhoneDialer';
import { PhoneMessaging } from './apps/PhoneMessaging';
import { PhoneBrowser } from './apps/PhoneBrowser';
import type { PhoneNavHandlers } from './PhoneFrame';

export interface AppLauncherProps {
  /** Whether apps are disabled (e.g., network not registered) */
  disabled?: boolean;
  /** Called whenever the set of active OS-nav handlers changes (i.e. when the
   *  user enters or exits an app). The parent wires these into PhoneFrame so
   *  the bottom Back / Home / Recents buttons actually navigate. */
  onNavHandlersChange?: (handlers: PhoneNavHandlers) => void;
}

type AppType = 'launcher' | 'phone' | 'messaging' | 'browser';

interface AppConfig {
  id: AppType;
  name: string;
  icon: React.ReactNode;
  color: string;
  component: React.ComponentType<{ onBack: () => void; disabled?: boolean }>;
}

const APPS: AppConfig[] = [
  {
    id: 'phone',
    name: 'Phone',
    icon: <Phone className="w-7 h-7" />,
    color: 'bg-tech-4g-bg text-tech-4g',
    component: PhoneDialer
  },
  {
    id: 'messaging',
    name: 'Messages',
    icon: <MessageSquare className="w-7 h-7" />,
    color: 'bg-status-success-bg text-status-success',
    component: PhoneMessaging
  },
  {
    id: 'browser',
    name: 'Browser',
    icon: <Globe className="w-7 h-7" />,
    color: 'bg-tech-5g-bg text-tech-5g',
    component: PhoneBrowser
  }
];

/**
 * AppIcon Component
 * Individual app icon with label
 */
interface AppIconProps {
  app: AppConfig;
  onClick: () => void;
  disabled: boolean;
}

const AppIcon: React.FC<AppIconProps> = ({ app, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center gap-1.5 p-2 rounded-lg transition-colors',
        'hover:bg-muted active:scale-95',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Icon container */}
      <div
        className={cn(
          'w-14 h-14 rounded-xl flex items-center justify-center shadow-md',
          app.color
        )}
      >
        {app.icon}
      </div>

      {/* App name */}
      <span className="text-sm font-medium text-foreground">{app.name}</span>
    </button>
  );
};

/**
 * AppLauncher Component
 * Main component that manages app navigation
 */
export const AppLauncher: React.FC<AppLauncherProps> = ({ disabled = false, onNavHandlersChange }) => {
  const [currentApp, setCurrentApp] = useState<AppType>('launcher');

  const handleAppClick = (appId: AppType) => {
    if (!disabled) {
      setCurrentApp(appId);
    }
  };

  const handleBack = useCallback(() => {
    setCurrentApp('launcher');
  }, []);

  // Emit OS-nav handlers to the parent whenever the active app changes. At
  // the launcher all three buttons are inactive (nothing to go back to);
  // inside an app all three return to the launcher.
  useEffect(() => {
    if (!onNavHandlersChange) return;
    if (currentApp === 'launcher') {
      onNavHandlersChange({});
    } else {
      onNavHandlersChange({
        onBack: handleBack,
        onHome: handleBack,
        onRecents: handleBack,
      });
    }
  }, [currentApp, handleBack, onNavHandlersChange]);

  // Render launcher grid
  if (currentApp === 'launcher') {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="grid grid-cols-3 gap-4 p-5">
          {APPS.map((app) => (
            <AppIcon
              key={app.id}
              app={app}
              onClick={() => handleAppClick(app.id)}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
    );
  }

  // Render current app
  const currentAppConfig = APPS.find(app => app.id === currentApp);
  if (!currentAppConfig) {
    return null;
  }

  const AppComponent = currentAppConfig.component;

  return (
    <div className="h-full bg-background">
      <AppComponent onBack={handleBack} disabled={disabled} />
    </div>
  );
};

export default AppLauncher;
